/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    useMultiStyleConfig,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import textTileWithProductilesContainer from '../../../theme/components/project/textTileWithProductilesContainer'

// Utility function to strip HTML tags
const stripHtmlTags = (str) => {
    if (!str) return ''
    return str.replace(/<\/?[^>]+(>|$)/g, '')
}

/**
 *text tile with powerReviews component
 */
export const TextTileWithPowerReviews = ({
    title,
    titleTextFontWeight,
    titleTextColor,
    titleTextStyle,
    subTitle,
    subTitleTextColor,
    subTitleTextFontWeight,
    subTitleTextStyle,
    subTitle1,
    subTitle1TextColor,
    subTitle1TextFontWeight,
    subTitle1TextStyle,
    script,
    htmlcode,
    custom
}) => {
    const styles = useMultiStyleConfig('TrendingProducts')
    const showBgImage = useBreakpointValue({base: true})
    const imageURL = custom?.backgroundImage
    const [bgImage, setBgImage] = useState('')
    const containerRef = useRef(null)

    useEffect(() => {
        if (!showBgImage) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    // Lazy-load background image
                    setBgImage(`url(${imageURL?.disBaseLink})`)
                    observer.disconnect()
                }
            },
            {
                rootMargin: '300px'
            }
        )

        if (containerRef.current) {
            observer.observe(containerRef.current)
        }

        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current)
            }
        }
    }, [showBgImage])

    useEffect(() => {
        if (!script) return

        const scriptTag = document.createElement('script')
        scriptTag.type = 'text/javascript'
        scriptTag.innerHTML = script

        const target = document.querySelector('.htmlCode')
        if (target) {
            target.appendChild(scriptTag)
        }

        return () => {
            if (target && scriptTag.parentNode === target) {
                target.removeChild(scriptTag)
            }
        }
    }, [script])

    return (
        <Box ref={containerRef} sx={textTileWithProductilesContainer}>
            <Box
                {...styles.trendingProducts}
                className="textTileWithProduct-container power-reviews-section"
                backgroundImage={bgImage}
                color={''}
            >
                <Box className="textTile-main-block" {...styles.trendingProductsWrapper}>
                    <Box className="textTile-inner-block">
                        {title && (
                            <Box
                                className="heading"
                                {...styles.heading}
                                color={titleTextColor?.value || 'black'}
                                fontStyle={titleTextStyle}
                                fontWeight={titleTextFontWeight}
                            >
                                {title}
                            </Box>
                        )}

                        {subTitle && (
                            <Box
                                className="textDescription"
                                {...styles.textDescription}
                                color={subTitleTextColor?.value || 'black'}
                                fontStyle={subTitleTextStyle}
                                fontWeight={subTitleTextFontWeight}
                            >
                                {subTitle}
                            </Box>
                        )}

                        {subTitle1 && (
                            <Box className="link-block">
                                <Box
                                    style={{
                                        color: subTitle1TextColor?.value,
                                        fontStyle: subTitle1TextStyle,
                                        fontWeight: subTitle1TextFontWeight
                                    }}
                                    {...styles.link}
                                >
                                    {stripHtmlTags(subTitle1)}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>

                <Box className="htmlCode textTile-products-block" {...styles.scrollerBlock}>
                    <Box>
                        {htmlcode !== undefined && (
                            <Box dangerouslySetInnerHTML={{__html: htmlcode}} />
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

TextTileWithPowerReviews.propTypes = {
    title: PropTypes.string,
    titleTextFontWeight: PropTypes.string,
    titleTextColor: PropTypes.object,
    titleTextStyle: PropTypes.string,
    subTitle: PropTypes.string,
    subTitleTextColor: PropTypes.object,
    subTitleTextFontWeight: PropTypes.string,
    subTitleTextStyle: PropTypes.string,
    subTitle1: PropTypes.string,
    subTitle1TextColor: PropTypes.object,
    subTitle1TextFontWeight: PropTypes.string,
    subTitle1TextStyle: PropTypes.string,
    script: PropTypes.string,
    htmlcode: PropTypes.string,
    custom: PropTypes.object
}

export default TextTileWithPowerReviews
